import React, { memo, useCallback, useState } from 'react'
import { SlideUp } from 'common-components'
import { useResponsive } from 'hooks/useResponsive'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import VideoGroupRow from './VideoGroupRow'
import VideoVariantList from './VideoVariantList'
import { IVideoDropdownModel, IVideoModelGroup } from '../types'
import * as Styled from './styled'

interface IVideoGroupItemProps {
  group: IVideoModelGroup
  selectedKey: string | null
  onSelect: (model: IVideoDropdownModel) => void
  labels: string[]
  onMobileExpandChange?: (open: boolean) => void
}

const VideoGroupItem: React.FC<IVideoGroupItemProps> = ({
  group,
  selectedKey,
  onSelect,
  labels,
  onMobileExpandChange
}) => {
  const isMobile = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)
  const isMultiVariant = group.models.length > 1
  const [triggerEl, setTriggerEl] = useState<HTMLDivElement | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  const containsSelected = group.models.some(m => m.selectKey === selectedKey)
  const isPremium = group.models.every(m => m.isPremium)

  const openDropdown = useCallback(() => {
    setIsOpen(true)
    if (isMobile) onMobileExpandChange?.(true)
  }, [isMobile, onMobileExpandChange])

  const closeDropdown = useCallback(() => {
    setIsOpen(false)
    if (isMobile) onMobileExpandChange?.(false)
  }, [isMobile, onMobileExpandChange])

  const handleTriggerClick = useCallback(() => {
    if (isMultiVariant) {
      if (isOpen) closeDropdown()
      else openDropdown()
      return
    }
    onSelect(group.models[0])
  }, [
    isMultiVariant,
    isOpen,
    group.models,
    onSelect,
    openDropdown,
    closeDropdown
  ])

  const handleVariantSelect = useCallback(
    (model: IVideoDropdownModel) => {
      closeDropdown()
      onSelect(model)
    },
    [onSelect, closeDropdown]
  )

  const isHoverEnabled = !isMobile && isMultiVariant
  const variantListProps = {
    models: group.models,
    selectedKey,
    onSelect: handleVariantSelect
  }

  return (
    <>
      <VideoGroupRow
        group={group}
        labels={labels}
        isActive={containsSelected || isOpen}
        isMultiVariant={isMultiVariant}
        isPremium={isPremium}
        onClick={handleTriggerClick}
        onMouseEnter={isHoverEnabled ? openDropdown : undefined}
        onMouseLeave={isHoverEnabled ? closeDropdown : undefined}
        triggerRef={setTriggerEl}
      />
      {!isMobile && isMultiVariant && (
        <Styled.SubmenuFlyout
          isOpen={isOpen}
          anchorEl={triggerEl}
          onClose={closeDropdown}
          options={{ position: 'right', alignmentY: 'top', extraOffset: 4 }}
          transition={{ duration: 0.18, ease: [0.32, 0.72, 0, 1] }}
        >
          <Styled.SubmenuPanel
            onMouseEnter={openDropdown}
            onMouseLeave={closeDropdown}
          >
            <VideoVariantList {...variantListProps} />
          </Styled.SubmenuPanel>
        </Styled.SubmenuFlyout>
      )}
      {isMobile && isMultiVariant && (
        <SlideUp isOpen={isOpen} onClose={closeDropdown}>
          <Styled.MobileSubmenuWrapper>
            <VideoVariantList {...variantListProps} />
          </Styled.MobileSubmenuWrapper>
        </SlideUp>
      )}
    </>
  )
}

export default memo(VideoGroupItem)
